import { useState } from 'react';
import { SUBSCRIPTION_PLANS, TOPUP_CONFIG } from '@/lib/stripeConfig';

function formatEur(n: number) {
  return new Intl.NumberFormat('de-DE', { style: 'decimal', maximumFractionDigits: 0 }).format(n) + ' €';
}

export const ROISection: React.FC = () => {
  const [objects, setObjects] = useState(5);
  const [images, setImages] = useState(10);

  const totalImages = objects * images;
  // Subscription model from stripeConfig: pick the genuinely cheapest plan for the
  // monthly image need — any shortfall beyond a plan's included credits is topped
  // up at 1,50 €/Credit. A smaller plan + top-ups can beat the next tier, so we
  // compare the total cost of every plan instead of just the first one that fits.
  const planCostCents = (p: (typeof SUBSCRIPTION_PLANS)[number]) =>
    p.monthlyCents + Math.max(0, totalImages - p.imageCredits) * TOPUP_CONFIG.unitCents;
  const plan = SUBSCRIPTION_PLANS.reduce((best, p) =>
    planCostCents(p) < planCostCents(best) ? p : best
  );
  const estageCents = planCostCents(plan);
  const otherCost = totalImages * 30;
  const estageCost = Math.ceil(estageCents / 100);
  const savings = otherCost - estageCost;
  const savingsPct = Math.round((savings / otherCost) * 100);
  const pricePerImageLabel =
    `${plan.name}-Abo · ≈ ` +
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(estageCents / 100 / totalImages) +
    ' / Bild';

  const styleSlider = (value: number, min: number, max: number) => ({
    background: `linear-gradient(to right, #FE6F17 0%, #FE6F17 ${((value - min) / (max - min)) * 100}%, #EBEBE6 ${((value - min) / (max - min)) * 100}%, #EBEBE6 100%)`,
  });

  return (
    <section className="lp-roi-section" id="roi">
      <div className="lp-roi-inner">
        <div className="lp-roi-layout">
          <div className="lp-roi-info">
            <div className="lp-roi-badge">Warum Estage?</div>
            <h2>Digitales Staging kostet einen Bruchteil</h2>
            <p>Andere Anbieter von digitalem Staging verlangen 20-50 EUR pro Bild. Mit Estage stagst du ein komplettes Objekt für einen Bruchteil — und behältst die volle Kontrolle.</p>
            <div className="lp-roi-highlights">
              <div className="lp-roi-highlight">
                <div className="lp-roi-highlight-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div className="lp-roi-highlight-text">
                  <strong>Sekunden statt Tage</strong>
                  <span>Andere Anbieter brauchen 24-48h. Estage liefert in unter 30 Sekunden.</span>
                </div>
              </div>
              <div className="lp-roi-highlight">
                <div className="lp-roi-highlight-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div className="lp-roi-highlight-text">
                  <strong>Volle Kontrolle</strong>
                  <span>Stil, Overlay und Branding selbst bestimmen — ohne Wartezeit auf Revisionen.</span>
                </div>
              </div>
              <div className="lp-roi-highlight">
                <div className="lp-roi-highlight-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>
                  </svg>
                </div>
                <div className="lp-roi-highlight-text">
                  <strong>Bis zu 98 % günstiger</strong>
                  <span>Ab 1,00 EUR pro Bild statt 20-50 EUR bei klassischen Staging-Diensten.</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lp-roi-calculator">
            <div className="lp-roi-calc-title">Deine Ersparnis berechnen</div>
            <div className="lp-roi-slider-group">
              <div className="lp-roi-slider-label">
                <span>Objekte pro Monat</span>
                <span className="lp-roi-slider-value">{objects}</span>
              </div>
              <input
                type="range"
                className="lp-roi-slider"
                min={1}
                max={30}
                value={objects}
                onChange={(e) => setObjects(Number(e.target.value))}
                style={styleSlider(objects, 1, 30)}
              />
            </div>
            <div className="lp-roi-slider-group">
              <div className="lp-roi-slider-label">
                <span>Bilder pro Objekt</span>
                <span className="lp-roi-slider-value">{images}</span>
              </div>
              <input
                type="range"
                className="lp-roi-slider"
                min={3}
                max={25}
                value={images}
                onChange={(e) => setImages(Number(e.target.value))}
                style={styleSlider(images, 3, 25)}
              />
            </div>
            <div className="lp-roi-divider" />
            <div className="lp-roi-results">
              <div className="lp-roi-result-card">
                <div className="lp-roi-result-label">Andere Anbieter</div>
                <div className="lp-roi-result-value">{formatEur(otherCost)}</div>
                <div className="lp-roi-result-sub">bei 30 € / Bild</div>
              </div>
              <div className="lp-roi-result-card">
                <div className="lp-roi-result-label">Mit Estage</div>
                <div className="lp-roi-result-value">{formatEur(estageCost)}</div>
                <div className="lp-roi-result-sub">{pricePerImageLabel}</div>
              </div>
              <div className="lp-roi-result-card highlight">
                <div className="lp-roi-result-label">Deine monatliche Ersparnis</div>
                <div className="lp-roi-result-value">{formatEur(savings)}</div>
                <div className="lp-roi-result-sub">{savingsPct} % günstiger</div>
              </div>
            </div>
            <div className="lp-roi-cta-note">Berechnung basiert auf durchschnittlichen Marktpreisen für digitales Staging</div>
          </div>
        </div>
      </div>
    </section>
  );
};
